import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import "./FormErrorLogsPage.css";

function getRole() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.role || "";
  } catch {
    return "";
  }
}

function isEnabled() {
  return localStorage.getItem("formErrorLogsEnabled") === "true";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-GB");
}

export default function FormErrorLogsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logs, setLogs] = useState([]);
  const [query, setQuery] = useState("");
  const [activeLog, setActiveLog] = useState(null);
  const [cleaningId, setCleaningId] = useState("");

  const role = getRole();
  const [enabled, setEnabled] = useState(isEnabled());

  useEffect(() => {
    const syncEnabled = () => setEnabled(isEnabled());
    window.addEventListener("formErrorLogsVisibilityChanged", syncEnabled);
    window.addEventListener("storage", syncEnabled);
    return () => {
      window.removeEventListener("formErrorLogsVisibilityChanged", syncEnabled);
      window.removeEventListener("storage", syncEnabled);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchLogs = async () => {
      setLoading(true);
      setError("");
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(`${baseUrl}/api/form-error-log`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!mounted) return;
        if (!res.ok || !data?.success) {
          setError(data?.message || "Failed to fetch form error logs");
          setLogs([]);
          return;
        }
        setLogs(Array.isArray(data.data) ? data.data : []);
      } catch {
        if (!mounted) return;
        setError("Failed to fetch form error logs");
        setLogs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchLogs();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((item) => {
      const searchText = [
        item?.formName,
        item?.userName,
        item?.createdAt,
        JSON.stringify(item?.error || {}),
        JSON.stringify(item?.deviceinfo || {}),
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return searchText.includes(q);
    });
  }, [logs, query]);

  const handleClean = async (logId) => {
    if (!logId) return;
    if (!window.confirm("Are you sure you want to clean this log?")) return;
    try {
      setCleaningId(logId);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/form-error-log/${logId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || "Failed to clean log");
        return;
      }
      setLogs((prev) => prev.filter((item) => item._id !== logId));
      if (activeLog?._id === logId) setActiveLog(null);
    } finally {
      setCleaningId("");
    }
  };

  if (role !== "admin" || !enabled) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <div className="form-error-logs-page">
      <div className="form-error-logs-card">
        <div className="form-error-logs-head">
          <h3>Form Error Logs</h3>
          <input
            type="text"
            placeholder="Search by form name, user name, error..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading ? <div className="form-error-logs-state">Loading logs...</div> : null}
        {!loading && error ? <div className="form-error-logs-state error">{error}</div> : null}

        {!loading && !error ? (
          <div className="form-error-logs-table-wrap">
            <table className="form-error-logs-table">
              <thead>
                <tr>
                  <th>Form Name</th>
                  <th>Submitted By</th>
                  <th>Error Message</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!filtered.length ? (
                  <tr>
                    <td colSpan={5} className="empty">No logs found</td>
                  </tr>
                ) : (
                  filtered.map((item) => (
                    <tr key={item._id}>
                      <td>{item.formName || "-"}</td>
                      <td>{item.userName || "-"}</td>
                      <td>{item?.error?.message || "Error object"}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td>
                        <div className="table-actions">
                          <button type="button" className="view-btn" onClick={() => setActiveLog(item)}>
                            View
                          </button>
                          <button
                            type="button"
                            className="clean-btn"
                            onClick={() => handleClean(item._id)}
                            disabled={cleaningId === item._id}
                          >
                            {cleaningId === item._id ? "Cleaning..." : "Clean"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {activeLog ? (
        <div className="form-error-modal-overlay" onClick={() => setActiveLog(null)}>
          <div className="form-error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-error-modal-head">
              <div className="form-error-modal-title-wrap">
                <h4>Log Details</h4>
              </div>
              <button type="button" onClick={() => setActiveLog(null)}>×</button>
            </div>
            <div className="form-error-modal-body">
              <div className="form-error-modal-grid">
                <div><span>Form Name</span><strong>{activeLog.formName || "-"}</strong></div>
                <div><span>Submitted By</span><strong>{activeLog.userName || "-"}</strong></div>
                <div><span>Created At</span><strong>{formatDateTime(activeLog.createdAt)}</strong></div>
              </div>
              <div className="json-block">
                <h5><i className="fa-solid fa-database" /> Body Data</h5>
                <pre>{JSON.stringify(activeLog.bodydata || {}, null, 2)}</pre>
              </div>
              <div className="json-block">
                <h5><i className="fa-solid fa-triangle-exclamation" /> Error</h5>
                <pre>{JSON.stringify(activeLog.error || {}, null, 2)}</pre>
              </div>
              <div className="json-block">
                <h5><i className="fa-solid fa-mobile-screen-button" /> Device Info</h5>
                <pre>{JSON.stringify(activeLog.deviceinfo || {}, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
